import React from 'react';
import FinautaPageBase from '../shared/FinautaPageBase';
import Title from '../shared/Title';
import styled, { css } from 'styled-components';
import SectionTitle from './SectionTitle';
import Quote from './Quote';
import Text from '../shared/Text';
import Table from '../shared/Table';
import VideoPlayer from './VideoPlayer';
import List from '../shared/List';
import { Accordion } from 'react-accessible-accordion';
import FAQuestion from '../landing/FAQuestion';
import TableOfContents from './TableOfContents';
import { background, space, layout } from 'styled-system';
import { Element } from 'react-scroll';
import ResultImage from '../shared/ResultImage';
import CTAButton from '../shared/CTAButton';
import PensionPlanShowoff from '../landing/PensionPlanShowoff';
import FollowingCTA from './FollowingCTA';

import backgroundImage1 from '../../images/backgrounds/section1.jpg';
import { palette, breakpoints, flexCenter } from '../../constants/styles';

const Introduction = styled.div`
  ${flexCenter}
  flex-direction: column;
`;

const Promotion = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  max-width: 400px;
  ${space}
`;

const SubSection = styled(Element)`
  justify-content: flex-start;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const Section = styled(Element)`
  margin: 80px 0px;
  ${background}
  ${(props) =>
    props.noPadding &&
    `
    width: 100vw;
    margin-bottom: 0px;
  `}
  ${space}
`;

const VideoPlayerContainer = styled.div`
  margin-top: 10px;
  align-self: flex-start;
  margin-bottom: 20px;
  width: 280px;

  ${breakpoints.tablet} {
    margin-bottom: 30px;
    width: 500px;
  }

  ${breakpoints.desktop} {
    margin-bottom: 40px;
    width: 650px;
  }
`;

const FAQs = {
  target: [
    {
      question: 'Esta guía es para ti si...',
      answer: (
        <>
          <Text type="pilarPage">
            Has cotizado en el IMSS y tu primera cotización fue antes del 1 de julio de 1997. Esto
            debido a que podrías ser acreedor a una pensión según la Ley del Seguro Social de 1973.
          </Text>
        </>
      ),
      metric: '',
      expanded: true,
    },
    {
      question: 'Esta guía NO es para ti si...',
      answer: (
        <>
          <Text type="pilarPage">
            Nunca has cotizado en el IMSS o tu primera cotización fue en o después del 1 de julio de
            1997. En tu caso, puedes obtener un estimado de cuánto será tu ingreso en el retiro y
            cómo mejorarlo aquí.
          </Text>
          <Text type="pilarPage">
            Te interesa saber más sobre pensiones del IMSS por accidente, enfermedad o invalidez.
            Esta guía contiene únicamente información relacionada con pensiones por vejez y cesantía
            en edad avanzada.
          </Text>
        </>
      ),
      metric: '',
    },
  ],
  requisitos: [
    {
      question: '¿Cuál es la diferencia entre pensión por cesantía y por vejez?',
      answer: (
        <>
          <VideoPlayerContainer>
            <VideoPlayer url="https://www.youtube.com/watch?v=6WR0ZfQXetk&list=PLR3Gc0jorI0KPhEWdwGAWyTI2ewhzUkEK&index=7&t=0s" />
          </VideoPlayerContainer>

          <Text type="pilarPage">
            La principal diferencia es la edad en la que solicitas tu pensión, la cual tiene un
            impacto en el monto de pensión que percibirás.
          </Text>

          <Text type="pilarPage">
            Si tienes entre 60 y 64 años, te corresponde solicitar una pensión por cesantía en edad
            avanzada. Mientras que si tienes 65 años de edad o más, te toca una pensión por vejez.
          </Text>

          <Text type="pilarPage">
            La pensión por cesantía tiene un descuento, comparada con la pensión por vejez debido a
            que se obtiene antes de los 65 años.
          </Text>
        </>
      ),
      metric: '',
    },
    {
      question: '¿Qué pasó el 1 de julio de 1997?',
      answer: (
        <>
          <VideoPlayerContainer>
            <VideoPlayer url="https://www.youtube.com/watch?v=oJK1_od821s&list=PLR3Gc0jorI0KPhEWdwGAWyTI2ewhzUkEK&index=6&t=0s" />
          </VideoPlayerContainer>

          <Text type="pilarPage">
            El 1 de julio de 1997 entró en vigor la nueva Ley del Seguro Social. Su principal cambio
            fue la modificación del esquema de pensión de los trabajadores.
          </Text>

          <Text type="pilarPage">
            Antes de la entrada en vigor de la nueva ley, el IMSS se encargaba de administrar las
            contribuciones de los trabajadores y de otorgar una pensión vitalicia a sus jubilados.
          </Text>

          <Text type="pilarPage">
            A partir del año 1997 se crean las Afores, que son responsables de administrar e
            invertir el ahorro para el retiro de los trabajadores afiliados al IMSS.
          </Text>
        </>
      ),
      metric: '',
    },
    {
      question: '¿Cuándo comencé a cotizar en el IMSS?',
      answer: (
        <>
          <Text type="pilarPage">
            Puedes revisar cuándo comenzaste a cotizar en el IMSS en tu reporte de semanas
            cotizadas, el cual puedes descargar aquí. Necesitarás tu CURP y Número de Seguridad
            Social (NSS).
          </Text>

          <Text type="pilarPage">
            En tu reporte de semanas cotizadas encontrarás el detalle de salario y semanas cotizadas
            por cada empleador de tu vida laboral.
          </Text>
        </>
      ),
      metric: '',
    },
    {
      question: '¿Qué es conservación de derechos?',
      answer: (
        <>
          <VideoPlayerContainer>
            <VideoPlayer url="https://www.youtube.com/watch?v=k8Hejit0KOc&list=PLR3Gc0jorI0KPhEWdwGAWyTI2ewhzUkEK&index=8&t=0s" />
          </VideoPlayerContainer>

          <Text type="pilarPage">
            La conservación de derechos es un estatus que concede el IMSS a sus asegurados después
            de una baja laboral y es un requisito para reclamar tu pensión. En caso de no conservar
            tus derechos ante el IMSS al momento de solicitar tu pensión, esta te será negada.
          </Text>

          <Text type="pilarPage">
            La duración del estatus de conservación de derechos es igual a la cuarta parte del
            tiempo total cotizado. Por ejemplo, si has cotizado 1,600 semanas, conservarás tus
            derechos para solicitar tu pensión por 400 semanas (= 1,600/4) después de la fecha de
            baja.
          </Text>

          <Text type="pilarPage">
            En caso de no estar en conservación de derechos, deberás cotizar de nuevo para poder
            recuperar tu vigencia y solicitar tu pensión. El tiempo que deberás cotizar para
            recuperar tus derechos de pensión dependerá del tiempo transcurrido desde tu última
            cotización. Si han pasado más de 6 años, deberás cotizar un mínimo de 52 semanas para
            recuperar tus derechos.
          </Text>
        </>
      ),
      metric: '',
    },
    {
      question: '¿Qué es régimen obligatorio?',
      answer: (
        <>
          <Text type="pilarPage">
            El régimen obligatorio es uno de los regímenes del Seguro Social, se compone
            principalmente de trabajadores vinculados a un patrón que tiene la obligación de
            asegurarlos ante el IMSS.
          </Text>
        </>
      ),
      metric: '',
    },
  ],
  requisitosMod40: [
    {
      question: '¿Qué hacer si actualmente cotizo con un patrón?',
      answer: (
        <>
          <Text type="pilarPage">
            En caso de que actualmente estés trabajando con un patrón y quieras darte de alta en
            Modalidad 40 tendrás que finalizar tu relación laboral.
          </Text>
          <Text type="pilarPage">
            Te recomendamos platicar con tu patrón al respecto y considerar que al finalizar tu
            relación laboral, perderás tu derecho a servicio médico.
          </Text>
        </>
      ),
      metric: '',
    },
    {
      question: '¿Qué hacer si llevo mucho tiempo sin cotizar con un patrón?',
      answer: (
        <>
          <Text type="pilarPage">
            Si han pasado más de 5 años desde tu última relación laboral, deberás cotizar con un
            patrón por lo menos 1 año para recuperar tu derecho de cotizar en Modalidad 40
          </Text>
        </>
      ),
      metric: '',
    },
  ],
  comparativaMod40: [
    {
      question: '¿Cuándo me conviene Modalidad 40?',
      answer: (
        <>
          <Text type="pilarPage">Cuando...</Text>
          <List
            textType="pilarPage"
            list={[
              'Tu salario actual de cotización diario es relativamente bajo',
              'Tienes los recursos necesarios para poder realizar las aportaciones al IMSS',
              'No tienes problema con perder tus derechos de servicio médico porque estás cubierto de otra manera',
              'Perdiste tu empleo y no quieres perder tu derecho a pensión',
            ]}
            textStyles={css`
              margin-bottom: 10px;
            `}
          />
        </>
      ),
      metric: '',
    },
    {
      question: '¿Qué es pago retroactivo en Modalidad 40?',
      answer: (
        <>
          <VideoPlayerContainer>
            <VideoPlayer url="https://www.youtube.com/watch?v=ztyR3sDzRDQ&list=PLR3Gc0jorI0KPhEWdwGAWyTI2ewhzUkEK&index=22&t=0s" />
          </VideoPlayerContainer>

          <Text type="pilarPage">
            El pago retroactivo en Modalidad 40 aplica para personas que actualmente no están
            cotizando con un patrón y que cumplen los requisitos de Modalidad 40. Consiste en
            realizar un pago único de las aportaciones correspondientes desde la fecha de baja al
            presente. De esta manera, se acumulan semanas al salario de cotización que se elija.
          </Text>
        </>
      ),
      metric: '',
    },
  ],
};

const chapters = [
  {
    id: 'metocapension',
    name: 'Sección 1',
    title: '¿Me toca pensión?',
  },
  {
    id: 'cuanto',
    name: 'Sección 2',
    title: '¿De cuánto será mi pensión?',
  },
  {
    id: 'mejorar',
    name: 'Sección 3',
    title: '¿Cómo mejorar mi pensión?',
  },
  {
    id: 'tramitar',
    name: 'Sección 4',
    title: '¿Cómo tramitar mi pensión?',
  },
];

export default function PensionIMSS() {
  return (
    <FinautaPageBase>
      <FollowingCTA
        width={['100vw']}
        CTAto={'/plan-de-pension'}
        tableOfContentsId="tabla"
        marginLeft={['-20px', '-80px', '-200px']}
      />
      <Section mt={'0px'} mb={'40px'}>
        <SectionTitle
          overlayStrength={0.6}
          backgroundImage={`url(${backgroundImage1})`}
          color={palette.primaryColor}
          fontColor="white"
          primary
          titleStyles={css`
            line-height: 1.2;
          `}
          mb="20px"
          title={['Pensiones IMSS:', 'La guía para lograr tu retiro ideal']}
        />
        <Introduction>
          <VideoPlayerContainer>
            <VideoPlayer url="https://www.youtube.com/watch?v=kPK9xNTS9to&list=PLR3Gc0jorI0KPhEWdwGAWyTI2ewhzUkEK&index=2&t=0s" />
          </VideoPlayerContainer>
        </Introduction>
        <Title level={3} align="left" style={{ alignSelf: 'flex-start' }} medium>
          ¿Esta guía es para mí?
        </Title>
        <VideoPlayerContainer>
          <VideoPlayer url="https://www.youtube.com/watch?v=uB2qSye-Yq4&list=PLR3Gc0jorI0KPhEWdwGAWyTI2ewhzUkEK&index=3&t=0s" />
        </VideoPlayerContainer>
        <Accordion accordion={true} style={{ width: '100%', marginTop: '10px', maxWidth: '750px' }}>
          {FAQs['target'].map((question) => (
            <FAQuestion
              questionLevel={5}
              event={question.metric}
              question={question}
              key={question.question}
              expanded={question.expanded}
            />
          ))}
        </Accordion>
      </Section>
      <Section
        background={'#f5f9f9'}
        name="tabla"
        marginLeft={['-20px', '-80px', '-200px']}
        noPadding
      >
        <TableOfContents chapters={chapters} />
      </Section>
      <Section marginTop={0} name={chapters[0].id}>
        <SectionTitle
          overlayStrength={0.3}
          color={palette.secondaryColor}
          title={[chapters[0].title]}
          name={chapters[0].name}
        />
        <Introduction>
          <Quote quote="El éxito depende del esfuerzo" author="Sófocles" title="poeta griego" />
          <VideoPlayerContainer>
            <VideoPlayer url="https://www.youtube.com/watch?v=NN2bvva_InI&list=PLR3Gc0jorI0KPhEWdwGAWyTI2ewhzUkEK&index=4&t=0s" />
          </VideoPlayerContainer>

          <Text type="pilarPage">
            En esta sección podrás encontrar los requisitos de pensión, un desglose de su
            significado y cómo podrían aplicar a tu caso.
          </Text>
        </Introduction>
        <Title level={3} align="left" style={{ alignSelf: 'flex-start' }} medium>
          Requisitos de pensión
        </Title>
        <VideoPlayerContainer>
          <VideoPlayer url="https://www.youtube.com/watch?v=B306rOUxmxI&list=PLR3Gc0jorI0KPhEWdwGAWyTI2ewhzUkEK&index=5&t=0s" />
        </VideoPlayerContainer>

        <List
          textType="pilarPage"
          list={[
            'Haber iniciado a cotizar en el IMSS antes del 1 de julio de 1997',
            'Tener cumplidos al menos 60 años de edad (pensión por cesantía) o 65 años de edad (pensión por vejez)',
            'Contar con vigencia de derechos o estar dentro del periodo de conservación de derechos ante el IMSS',
            'Cotizar un mínimo de 500 semanas en régimen obligatorio',
            'Estar dado de baja en el IMSS',
          ]}
          textStyles={css`
            margin-bottom: 10px;
          `}
        />
        <Title level={3} align="left" style={{ alignSelf: 'flex-start' }} medium>
          Preguntas: requisitos de pensión
        </Title>
        <Accordion accordion={true} style={{ width: '100%', marginTop: '10px', maxWidth: '750px' }}>
          {FAQs['requisitos'].map((question) => (
            <FAQuestion
              questionLevel={5}
              event={question.metric}
              question={question}
              key={question.question}
              expanded={question.expanded}
            />
          ))}
        </Accordion>
      </Section>
      <Section name={chapters[1].id}>
        <SectionTitle
          overlayStrength={0.3}
          color={palette.terciaryColor}
          title={[chapters[1].title]}
          name={chapters[1].name}
        />
        <Introduction>
          <Quote
            quote="El tiempo es más valioso que el dinero. Puedes obtener más dinero, pero no más tiempo"
            author="Jim Rohn"
            title="empresario estadounidense"
          />
          <VideoPlayerContainer>
            <VideoPlayer url="https://www.youtube.com/watch?v=7dL7nCsxvVA&list=PLR3Gc0jorI0KPhEWdwGAWyTI2ewhzUkEK&index=9&t=0s" />
          </VideoPlayerContainer>
          <Text type="pilarPage">
            En esta sección te explicaremos qué variables influyen en el cálculo de tu pensión. Si
            quieres un cálculo aproximado de tu pensión, usa nuestra calculadora de retiro gratuita.
            Si lo que buscas es un cálculo detallado acompañado de proyecciones en Modalidad 40,
            solicita un plan de pensión.
          </Text>
        </Introduction>
        <Title level={3} align="left" style={{ alignSelf: 'flex-start' }} medium>
          Variables clave para el cálculo de mi pensión
        </Title>
        <SubSection>
          <Title level={4} align="left" style={{ alignSelf: 'flex-start' }} medium>
            Edad de retiro
          </Title>
          <VideoPlayerContainer>
            <VideoPlayer url="https://www.youtube.com/watch?v=zPxfm0VqXxU&list=PLR3Gc0jorI0KPhEWdwGAWyTI2ewhzUkEK&index=10&t=0s" />
          </VideoPlayerContainer>
          <Text type="pilarPage">
            Al cumplir los requisitos de pensión, la ley establece que te podrás pensionar a partir
            de los 60 años. Sin embargo, tu pensión tendrá un descuento si te retiras antes de los
            65 años.
          </Text>
          <Text type="pilarPage">
            Si te retiras entre los 60 y 64 años, te corresponde solicitar una pensión por cesantía
            en edad avanzada. Mientras que si tienes 65 años de edad o más, te toca una pensión por
            vejez.
          </Text>
          <Text type="pilarPage">
            La pensión por cesantía tiene un descuento, comparada con la pensión por vejez debido a
            que se obtiene antes de los 65 años. El porcentaje de pensión varía dependiendo de la
            edad de pensión con base en la siguiente tabla:
          </Text>
          <Table
            mb={'20px'}
            maxWidth={['300px', '500px', '550px']}
            array={[
              ['edad', 'porcentaje de pensión'],
              ['60', '75%'],
              ['61', '80%'],
              ['62', '85%'],
              ['63', '90%'],
              ['64', '95%'],
              ['65+', '100%'],
            ]}
          />
          <Text type="pilarPage">
            Por ejemplo, si el cálculo de tu pensión es de $16,000 mensuales y tienes 60 años, tu
            pensión será de $12,000 mensuales (= $16,000*75%).
          </Text>
        </SubSection>
        <SubSection>
          <Title level={4} align="left" style={{ alignSelf: 'flex-start' }} medium>
            Semanas cotizadas
          </Title>
          <VideoPlayerContainer>
            <VideoPlayer url="https://www.youtube.com/watch?v=avVV0_RyZ5M&list=PLR3Gc0jorI0KPhEWdwGAWyTI2ewhzUkEK&index=11&t=0s" />
          </VideoPlayerContainer>
          <Text type="pilarPage">
            Las semanas cotizadas son una de las variables más importantes para el cálculo de tu
            pensión por dos razones. Primero, porque tener más de 500 semanas cotizadas es requisito
            para obtener tu pensión. Segundo, por el impacto que pueden tener en el monto final de
            tu pensión.
          </Text>
          <Text type="pilarPage">
            Las pensiones están compuestas por una “cuantía básica” e “incrementos anuales”.
            Mientras que la “cuantía básica” sólo depende de tu salario de cotización promedio, los
            “incrementos anuales” aumentan conforme aumentan tus semanas cotizadas.
          </Text>
          <Text type="pilarPage">Para ilustrar su impacto, supongamos el siguiente ejemplo:</Text>
          <List
            textType="pilarPage"
            list={[
              'Edad de retiro: 65 años',
              'Salario promedio diario: $450',
              'Información personal: casado sin hijos menores',
            ]}
            textStyles={css`
              margin-bottom: 10px;
            `}
          />
          <Text type="pilarPage">
            Supondremos 3 escenarios de semanas cotizadas: 550, 1,000 y 1,450.
          </Text>
          <Text type="pilarPage">Resultados por escenario de semanas cotizadas:</Text>
          <Table
            mb={'20px'}
            maxWidth={['300px', '500px', '550px']}
            boldFirstColumn
            array={[
              ['Semanas Cotizadas', '550', '1,000', '1,450'],
              ['Pensión mensual', '$4,048', '$7,499', '$10,759'],
              ['Cuantía básica', '$3,856', '$3,856', '$3,856'],
              ['Incremento anual', '$192', '$3,643', '$6,903'],
            ]}
            headerLimit={1}
          />

          <Text type="pilarPage">
            En la tabla anterior podemos observar que la cuantía básica en los 3 escenarios se
            mantiene constante en $3,856; sin embargo, la pensión mensual aumenta debido al
            incremento anual.
          </Text>
          <Text type="pilarPage">
            El incremento anual con 550 semanas cotizadas representa el 5% de la pensión total de
            $4,048 mensuales, mientras que con 1,450 semanas cotizadas representa el 64% de la
            pensión total de $10,759 mensuales.
          </Text>
          <Text type="pilarPage">
            Por lo tanto, entre más sean tus semanas de cotización mayor será tu pensión.
          </Text>
        </SubSection>
        <SubSection>
          <Title level={4} align="left" style={{ alignSelf: 'flex-start' }} medium>
            Salario promedio
          </Title>
          <VideoPlayerContainer>
            <VideoPlayer url="https://www.youtube.com/watch?v=PSixs_Sb9S4&list=PLR3Gc0jorI0KPhEWdwGAWyTI2ewhzUkEK&index=12&t=0s" />
          </VideoPlayerContainer>
          <Text type="pilarPage">
            Después del número de semanas cotizadas, el salario promedio es la variable más
            importante para el cálculo de tu pensión.
          </Text>
          <Text type="pilarPage">
            La pensión se calcula sobre el promedio de las últimas 250 semanas cotizadas, que
            representa aproximadamente 5 años. Esto implica que el salario percibido previo a las
            últimas 250 semanas es irrelevante para fines del cálculo de tu pensión.
          </Text>
          <Text type="pilarPage">
            Recordemos de la sección anterior que tu pensión se compone de dos partes, una “cuantía
            básica” e “incrementos anuales”. A diferencia de las semanas cotizadas, el salario
            promedio influye en ambas partes. Por lo que si tu salario actual es bajo, aumentar tu
            salario promedio puede incrementar radicalmente tu pensión.
          </Text>
          <Text type="pilarPage">
            Por lo tanto, entre más alto sea tu salario durante tus últimas 250 semanas cotizadas,
            mayor será tu pensión.
          </Text>
        </SubSection>
        <SubSection>
          <Title level={4} align="left" style={{ alignSelf: 'flex-start' }} medium>
            Asignaciones familiares y ayuda asistencial
          </Title>

          <VideoPlayerContainer>
            <VideoPlayer url="https://www.youtube.com/watch?v=1b856TK5O6k&list=PLR3Gc0jorI0KPhEWdwGAWyTI2ewhzUkEK&index=13&t=0s" />
          </VideoPlayerContainer>
          <Text type="pilarPage">
            La ley señala que las asignaciones familiares son una ayuda por concepto de carga
            familiar y se da a los beneficiarios del pensionado, de acuerdo con las siguientes
            reglas:
          </Text>
          <List
            textType="pilarPage"
            list={[
              '**Pareja:** tu pensión aumenta 15% si tienes pareja (espos@ o concubin@)',
              '**Hijos menores:** tu pensión aumenta 10% por hijo menor de 16 años',
              '**Padres:** En caso de no tener pareja ni hijos en el retiro, tu pensión aumenta 10% por padre económicamente dependiente',
              '**Soledad:** En caso de no tener pareja, hijos ni padres en el retiro, tu pensión aumenta 15% por concepto de ayuda asistencial',
            ]}
            textStyles={css`
              margin-bottom: 10px;
            `}
          />
        </SubSection>
        <Title level={3} align="left" style={{ alignSelf: 'flex-start' }} medium>
          Cómo calcular mi pensión
        </Title>
        <Text type="pilarPage">
          Una vez entendidas las variables clave para el cálculo de pensión, estás listo para
          calcularla. En esta sección te mostraremos diversos recursos para que puedas obtener
          montos aproximados de tu pensión.
        </Text>
        <SubSection>
          <Title level={4} align="left" style={{ alignSelf: 'flex-start' }} medium>
            Pensión máxima y mínima
          </Title>
          <VideoPlayerContainer>
            <VideoPlayer url="https://www.youtube.com/watch?v=19QZin23EnA&list=PLR3Gc0jorI0KPhEWdwGAWyTI2ewhzUkEK&index=14&t=6s" />
          </VideoPlayerContainer>
          <Text type="pilarPage">
            Sujeto a que cumples con los requisitos de pensión, es importante comprender los límites
            inferior y superior entre las que se encontrará tu pensión.
          </Text>
          <Title level={5} align="left" style={{ alignSelf: 'flex-start' }} medium>
            Pensión mínima IMSS
          </Title>
          <Text type="pilarPage">
            La pensión mínima garantizada es de un salario mínimo general mensual. Por ejemplo, en
            2020 el salario mínimo es de $123.22 pesos diarios, por lo que la pensión mínima mensual
            es de aproximadamente $3,746.
          </Text>
          <Text type="pilarPage">
            El salario mínimo es actualizado anualmente conforme al Índice Nacional de Precios al
            Consumidor (INPC) publicado por el Instituto Nacional de Estadística y Geografía
            (INEGI).
          </Text>
          <Title level={5} align="left" style={{ alignSelf: 'flex-start' }} medium>
            Pensión máxima IMSS
          </Title>
          <Text type="pilarPage">
            La pensión máxima que establece la ley es de 25 Unidades de Medida y Actualización (UMA)
            mensuales. Por ejemplo, en 2020 el valor de la UMA es de $86.88 pesos, por lo que la
            pensión máxima mensual es de aproximadamente $66,029.
          </Text>
          <Text type="pilarPage">
            La UMA es actualizada anualmente y su valor es publicado por el Instituto Nacional de
            Estadística y Geografía (INEGI).
          </Text>
        </SubSection>
        <SubSection>
          <Title level={4} align="left" style={{ alignSelf: 'flex-start' }} medium>
            Cálculo aproximado: Calculadora de Retiro
          </Title>
          <VideoPlayerContainer>
            <VideoPlayer url="https://www.youtube.com/watch?v=3qmYbk_gVL4&list=PLR3Gc0jorI0KPhEWdwGAWyTI2ewhzUkEK&index=15&t=4s" />
          </VideoPlayerContainer>
          <Text type="pilarPage">
            Si lo que buscas es obtener un cálculo aproximado de cuánto será tu pensión, te
            recomendamos usar nuestra calculadora de retiro.
          </Text>
          <Text type="pilarPage">
            Nuestra calculadora entrega un cálculo aproximado con base en información que tú
            introduzcas y puede ser usada por cualquier mexicano mayor de edad, ya que es válida
            para personas que comenzaron a cotizar en el IMSS antes y después de 1997.
          </Text>
          <Text type="pilarPage">
            A diferencia de la calculadora del IMSS, nuestra calculadora proyecta las semanas
            cotizadas que tendrás hasta la edad de tu retiro y te muestra cuánto podría aumentar tu
            pensión con Modalidad 40.
          </Text>
          <Promotion mb={['40px']}>
            <ResultImage maxWidth={['280px']} mt={['20px']} mb={['20px']} tool="retiro" />
            <CTAButton
              event="pilarpage/retirement-tool"
              internalLink={`/retiro/empezar`}
              buttonText="Empezar"
            />
          </Promotion>
          <Title level={5}>¿Cómo funciona?</Title>
          <List
            textType="pilarPage"
            list={[
              'Haz click en empezar',
              'Ingresa tus datos',
              'Obtén un resultado aproximado de tu pensión',
            ]}
            textStyles={css`
              margin-bottom: 10px;
            `}
            type="ol"
          />
        </SubSection>
        <SubSection>
          <Title level={4} align="left" style={{ alignSelf: 'flex-start' }} medium>
            Cálculo detallado: Plan de Pensión
          </Title>
          <VideoPlayerContainer>
            <VideoPlayer url="https://www.youtube.com/watch?v=KwkyvILxRpI&list=PLR3Gc0jorI0KPhEWdwGAWyTI2ewhzUkEK&index=16&t=0s" />
          </VideoPlayerContainer>
          <Text type="pilarPage">
            Si lo que buscas es obtener un cálculo detallado de cuánto será tu pensión si sigues
            como vas y cómo mejorarla, tu opción es solicitar un Plan de Pensión generado por
            expertos en pensiones.
          </Text>
          <Text type="pilarPage">
            A diferencia de un cálculo aproximado de tu pensión, un Plan de Pensión produce sus
            resultados de información real sobre tu historial laboral del IMSS. Al limitar la
            cantidad de supuestos en el cálculo, se logra que sus resultados sean mucho más
            precisos.
          </Text>
          <Text type="pilarPage">
            En Finauta, generamos los planes más completos y de menor precio debido a nuestro
            enfoque tecnológico que reduce nuestros costos. Esto con el objetivo de ayudar a la
            mayor cantidad de mexicanos a lograr su retiro ideal.
          </Text>
          <Title level={5}>Nuestro plan de pensión contiene:</Title>
          <List
            textType="pilarPage"
            list={[
              'Cálculo detallado de pensión en situación actual',
              'Cálculo detallado de 3 escenarios en Modalidad 40',
              'Análisis costo-beneficio de Modalidad 40',
              'Riesgos y áreas de oportunidad de tu situación',
            ]}
            textStyles={css`
              margin-bottom: 10px;
            `}
          />
          <Text type="pilarPage">
            Tu plan incluye la asesoría de un Asesor Financiero experto en pensiones para explicar
            tu plan y resolver tus dudas.
          </Text>
          <Title level={5}>Ejemplo de plan de pensión:</Title>
          <VideoPlayerContainer>
            <VideoPlayer url="https://www.youtube.com/watch?v=dfs1OF0bSYk&list=PLR3Gc0jorI0KPhEWdwGAWyTI2ewhzUkEK&index=17&t=0s" />
          </VideoPlayerContainer>
          <Promotion mb={['40px']}>
            <PensionPlanShowoff maxWidth={['300px']} pageOrigin="PilarPage" />
          </Promotion>

          <Title level={5}>¿Cómo funciona?</Title>
          <List
            textType="pilarPage"
            list={[
              'Haz click en quiero mi plan',
              'Deja tus datos de contacto',
              'Te contactaremos para recabar tus datos y generar tu plan',
              'Al realizar tu pago, recibirás tu plan de pensión',
              'Agenda una llamada con tu Asesor Financiero',
              'Relájate, tu retiro está cubierto',
            ]}
            textStyles={css`
              margin-bottom: 10px;
            `}
            type="ol"
          />
        </SubSection>
      </Section>
      <Section name={chapters[2].id}>
        <SectionTitle
          color={palette.primaryColor}
          title={[chapters[2].title]}
          name={chapters[2].name}
        />
        <Introduction>
          <Quote
            quote="En la vida hay algo peor que el fracaso: el no haber intentado nada"
            author="Franklin D. Roosevelt"
            title="32.<sup>o</sup> presidente de los Estados Unidos"
          />
          <VideoPlayerContainer>
            <VideoPlayer url="https://www.youtube.com/watch?v=cue0YzU24UM&list=PLR3Gc0jorI0KPhEWdwGAWyTI2ewhzUkEK&index=18&t=0s" />
          </VideoPlayerContainer>
          <Text type="pilarPage">
            De manera fundamental, puedes mejorar tu pensión incrementando tu número de semanas
            cotizadas y/o aumentando tu salario de cotización promedio.
          </Text>
          <Text type="pilarPage">
            Existen dos maneras de tener un impacto en tus semanas cotizadas y tu salario de
            cotización promedio:
          </Text>
          <List
            textType="pilarPage"
            list={['Seguir cotizando con un patrón', 'Comenzar a cotizar en Modalidad 40']}
            textStyles={css`
              margin-bottom: 10px;
            `}
            type="ol"
          />
        </Introduction>
        <Title level={3} align="left" style={{ alignSelf: 'flex-start' }} medium>
          Cotizar con un patrón
        </Title>
        <SubSection>
          <VideoPlayerContainer>
            <VideoPlayer url="https://www.youtube.com/watch?v=N128e06-xdw&list=PLR3Gc0jorI0KPhEWdwGAWyTI2ewhzUkEK&index=19&t=0s" />
          </VideoPlayerContainer>
          <Text type="pilarPage">
            Seguir cotizando con un patrón es la opción más natural y sencilla de mejorar tu pensión
            ya que no involucra ningún trámite extra ante el IMSS.
          </Text>
          <Text type="pilarPage">
            En caso de estar actualmente cotizando con un patrón, esta opción consiste en seguir
            trabajando y acumulando semanas. En caso de no estar actualmente cotizando con un
            patrón, esta opción requiere que te des de alta con uno.
          </Text>
          <Text type="pilarPage">
            Atención: Sabemos que buscar trabajo conforme pasa el tiempo puede ser difícil. Por lo
            que darte de alta con un amigo o familiar que tenga una empresa puede sonar como una
            buena idea, pero en realidad es riesgoso.
          </Text>
          <Text type="pilarPage">
            El IMSS suele investigar casos en los que pudiera existir “simulación laboral” en donde
            básicamente haces como que trabajas, cuando en realidad no lo estás haciendo. Esto pone
            en riesgo tu derecho de pensión y al empleador.
          </Text>
        </SubSection>
        <Title level={3} align="left" style={{ alignSelf: 'flex-start' }} medium>
          Cotizar en Modalidad 40
        </Title>
        <SubSection>
          <VideoPlayerContainer>
            <VideoPlayer url="https://www.youtube.com/watch?v=by9-_u8c9NA&list=PLR3Gc0jorI0KPhEWdwGAWyTI2ewhzUkEK&index=20&t=0s" />
          </VideoPlayerContainer>
          <Text type="pilarPage">
            La segunda opción para mejorar tu pensión, es darte de alta en la Continuación
            Voluntaria al Régimen Obligatorio, también conocida como Modalidad 40.
          </Text>
          <Text type="pilarPage">
            La segunda opción para mejorar tu pensión, es darte de alta en la Continuación
            Voluntaria al Régimen Obligatorio, también conocida como Modalidad 40.
          </Text>
          <Title level={4} align="left" style={{ alignSelf: 'flex-start' }}>
            ¿Qué es Modalidad 40?
          </Title>
          <Text type="pilarPage">
            La Modalidad 40 del IMSS es un esquema que te permite realizar aportaciones voluntarias
            con el salario de cotización que tú elijas, y por lo tanto mejorar tu pensión.
          </Text>
          <Text type="pilarPage">
            Esta modalidad fue creada para los que cotizan en el IMSS y fueron dados de baja, pero
            no quieren perder su derecho de pensión.
          </Text>
          <Title level={4} align="left" style={{ alignSelf: 'flex-start' }}>
            Requisitos de Modalidad 40
          </Title>
          <Text type="pilarPage">
            Para darte de alta en Modalidad 40 debes reunir los siguientes requisitos:
          </Text>
          <List
            textType="pilarPage"
            list={[
              'No estar cotizando con un patrón',
              'Haber cotizado con un patrón por lo menos 52 semanas en los últimos 5 años',
            ]}
            textStyles={css`
              margin-bottom: 10px;
            `}
          />
          <Title level={4} align="left" style={{ alignSelf: 'flex-start' }}>
            Preguntas frecuentes
          </Title>
          <Accordion
            accordion={true}
            style={{ width: '100%', marginTop: '10px', maxWidth: '750px' }}
          >
            {FAQs['requisitosMod40'].map((question) => (
              <FAQuestion
                questionLevel={5}
                event={question.metric}
                question={question}
                key={question.question}
                expanded={question.expanded}
              />
            ))}
          </Accordion>
        </SubSection>
        <Title level={3} align="left" style={{ alignSelf: 'flex-start' }} medium>
          Comparativa de opciones
        </Title>
        <VideoPlayerContainer>
          <VideoPlayer url="https://www.youtube.com/watch?v=wSpwzWIlDOM&list=PLR3Gc0jorI0KPhEWdwGAWyTI2ewhzUkEK&index=21&t=0s" />
        </VideoPlayerContainer>
        <SubSection>
          <Title level={4} align="left" style={{ alignSelf: 'flex-start' }}>
            Tabla comparativa
          </Title>
          <Table
            mb={'20px'}
            maxWidth={['300px', '500px', '550px']}
            array={[
              ['Cotizar con un patrón', 'Modalidad 40'],
              ['No eliges tu salario de cotización', 'Tú eliges tu salario de cotización'],
              [
                'Tu patrón realiza la mayor parte de tus aportaciones al IMSS',
                'Tú realizas el 100% de tus aportaciones al IMSS',
              ],
              [
                'Mantienes tus derechos de servicio médico e Infonavit',
                'Pierdes tus derechos de servicio médico e Infonavit',
              ],
              ['No requieres realizar un trámite', 'Requiere que realices un trámite'],
            ]}
          />
          <Title level={5} align="left" style={{ alignSelf: 'flex-start' }}>
            Atención:
          </Title>
          <List
            textType="pilarPage"
            list={[
              'Si decides cotizar con un patrón y tu salario es menor al promedio de los últimos 5 años, es posible que disminuyas tu pensión.',
              'Si decides darte de alta en Modalidad 40, podrías realizar un pago retroactivo para recuperar semanas de cotización con el salario que tú elijas',
            ]}
            textStyles={css`
              margin-bottom: 10px;
            `}
          />
          <Title level={4} align="left" style={{ alignSelf: 'flex-start' }}>
            Preguntas frecuentes
          </Title>
          <Accordion
            accordion={true}
            style={{ width: '100%', marginTop: '10px', maxWidth: '750px' }}
          >
            {FAQs['comparativaMod40'].map((question) => (
              <FAQuestion
                questionLevel={5}
                event={question.metric}
                question={question}
                key={question.question}
                expanded={question.expanded}
              />
            ))}
          </Accordion>
        </SubSection>
        <Title level={3} align="left" style={{ alignSelf: 'flex-start' }} medium>
          Ejemplo de pensión con Modalidad 40
        </Title>
        <SubSection>
          <VideoPlayerContainer>
            <VideoPlayer url="https://www.youtube.com/watch?v=uZRP1uaTaYM&list=PLR3Gc0jorI0KPhEWdwGAWyTI2ewhzUkEK&index=23&t=0s" />
          </VideoPlayerContainer>
          <Text type="pilarPage">
            Para entender mejor cómo mejorar tu pensión analizaremos el siguiente ejemplo.
          </Text>
          <Text type="pilarPage">
            Fernanda trabaja como gerente de una sucursal bancaria y a sus 55 años pierde su
            trabajo. Al momento de perder su trabajo, ella cuenta con 1,200 semanas cotizadas y un
            salario diario de cotización promedio de los últimos 5 años de $300. Asimismo, ella sabe
            que se quiere pensionar a los 60 años.
          </Text>
          <Text type="pilarPage">
            Fernanda se ve en la situación de decidir si buscar otro trabajo para seguir acumulando
            semanas de cotización, darse de alta en Modalidad 40 o simplemente esperar a cumplir 60
            años para tramitar su pensión.
          </Text>
          <Text type="pilarPage">
            Para que Fernanda tome una decisión informada, decide adquirir un plan de pensión en
            donde descubre de cuánto será su pensión si sigue como va, con cuánto se podría
            pensionar si regresa a trabajar con el mismo salario de cotización diario de $300 que
            tenía y, finalmente, con cuánto se podría pensionar si se da de alta en Modalidad 40 con
            un salario de cotización diario mayor de $600.
          </Text>
          <Text type="pilarPage">Resumen de estudio de pensión a los 60 años:</Text>
          <Table
            mb={'20px'}
            maxWidth={['300px', '500px', '550px']}
            boldFirstColumn
            array={[
              ['', 'Situación actual', 'Cotizar con un patrón', 'Modalidad 40'],
              ['Semanas cotizadas', '1,200', '1,460', '1,460'],
              ['Salario diario promedio', '$300', '$300', '$600'],
              ['Pensión mensual', '$6,750', '$7,840', '$13,700'],
              ['Inversión acumulada', '$0', '$0', '$110,300'],
            ]}
          />
          <Text type="pilarPage">
            Si Fernanda decide mantenerse en su situación actual; es decir, desempleada de los 55 a
            los 60 años, su pensión a los 60 años será de $6,750 mensuales. Sus semanas cotizadas y
            su salario promedio permanecerán iguales en 1,200 y $300 respectivamente.
          </Text>
          <Text type="pilarPage">
            También podemos ver que si ella se da de alta con el mismo salario de cotización diario
            que tenía con su último empleador de $300 diarios, ella se retirará con $7,840 mensuales
            y sus semanas de cotización aumentarán en 260 (52 semanas x 5 años).
          </Text>
          <Text type="pilarPage">
            Finalmente, podemos apreciar que si Fernanda se da de alta en Modalidad 40 y empieza a
            hacer aportaciones al IMSS a sus 55 años con un salario de cotización de $600 diarios,
            ella se podría retirar con una pensión de $13,700 mensuales. Esto debido a que sus
            semanas de cotización aumentarán en 260 (52 semanas x 5 años) y su salario de cotización
            promedio subirá a $600 diarios.
          </Text>
          <Text type="pilarPage">
            Para tomar la mejor decisión, Fernanda consideró la inversión acumulada que implicaría
            realizar aportaciones en Modalidad 40, los salarios perdidos por dejar de cotizar, el
            impacto que podría tener en su bienestar el dejar de tener derecho a servicio médico y
            sus planes para el retiro.
          </Text>
          <Text type="pilarPage">
            Dado que Fernanda tiene un guardadito y cuenta con derecho a servicio médico por parte
            de su pareja, decidió darse de alta en Modalidad 40 para mejorar su pensión.
          </Text>
        </SubSection>
      </Section>
      <Section name={chapters[3].id} mb={'80px'}>
        <SectionTitle
          overlayStrength={0.3}
          color={palette.sweetPurple}
          title={[chapters[3].title]}
          name={chapters[3].name}
        />
        <Introduction>
          <Quote
            quote="Este no es el final, ni siquiera es el principio del final. Pero es, tal vez, el final del principio"
            author="Winston Churchill"
            title="político británico"
          />
          <VideoPlayerContainer>
            <VideoPlayer url="https://www.youtube.com/watch?v=sHeyzGj5ZT8&list=PLR3Gc0jorI0KPhEWdwGAWyTI2ewhzUkEK&index=24&t=0s" />
          </VideoPlayerContainer>
          <Text type="pilarPage">
            En el pasado, el trámite de pensión en el IMSS era un proceso tardado y poco
            transparente. Sin embargo, en los últimos años el Instituto se ha esforzado en hacer el
            proceso más rápido y sencillo.
          </Text>
          <Text type="pilarPage">
            En esta sección te explicaremos el proceso digital de trámite de pensión así como el
            proceso de solicitud de sub-cuentas de tu Afore.
          </Text>
        </Introduction>
        <Title level={3} align="left" style={{ alignSelf: 'flex-start' }} medium>
          Trámite de pensión digital
        </Title>
        <SubSection>
          <VideoPlayerContainer>
            <VideoPlayer url="https://www.youtube.com/watch?v=9yUeustUFIA&list=PLR3Gc0jorI0KPhEWdwGAWyTI2ewhzUkEK&index=25&t=0s" />
          </VideoPlayerContainer>
          <Text type="pilarPage">
            En el trámite digital de tu pensión, lo primero que tienes que hacer es ingresar a Mi
            Pensión Digital del IMSS. Aquí el Insituto te recomienda una serie de acciones a
            realizar antes de iniciar el trámite:
          </Text>
          <List
            textType="pilarPage"
            list={[
              '**Compara tus datos.** Asegúrate que tus datos personales entre el IMSS y tu AFORE sean iguales',
              '**Consulta tus semanas cotizadas.** Verifica las semanas cotizadas que tienes registradas en el IMSS',
              '**Consulta tus beneficiarios.** Revisa que la información de tus beneficiarios es correcta',
            ]}
            textStyles={css`
              margin-bottom: 10px;
            `}
            type="ol"
          />
          <Text type="pilarPage">
            Después de haber revisado tus datos y que todo sea correcto, podrás iniciar el trámite.
            Tienes que tener a la mano los datos de tu CURP, Número de Seguridad Social, correo
            electrónico y la cuenta CLABE en la cuál vas a querer que se deposite tu pensión
            mensual.
          </Text>
          <Text type="pilarPage">
            Una vez que llenas todos los datos, el IMSS te dará una proyección de pensión (no es
            definitiva) y te dará una cita para que procedas a firmar tu resolución de pensión.
            Usualmente el trámite de la firma toma menos de 3 horas.
          </Text>
          <Text type="pilarPage">
            En resumen, el sitio de Mi Pensión Digital te permitirá, a través de internet, iniciar
            el proceso para tramitar tu pensión. El resultado de la verificación te dará certidumbre
            sobre los siguientes pasos y posibles errores.
          </Text>
        </SubSection>
        <Title level={4} align="left" style={{ alignSelf: 'flex-start' }}>
          Posibles problemas
        </Title>
        <SubSection>
          <VideoPlayerContainer>
            <VideoPlayer url="https://www.youtube.com/watch?v=ygMIxTNAJDQ&list=PLR3Gc0jorI0KPhEWdwGAWyTI2ewhzUkEK&index=26&t=0s" />
          </VideoPlayerContainer>
          <Title level={5}>El sistema no genera tu proyección de pensión</Title>
          <Text type="pilarPage">
            Existe la posibilidad de que el sistema no genere tu proyección de pensión. Si esto
            ocurre, es posible que con base en la información que cuenta el IMSS, NO eres candidato
            a obtener una pensión.
          </Text>
          <Text type="pilarPage">
            Si crees que el sistema tiene un error, debes acudir a la Subdelegación que te
            corresponde y realizar una aclaración. Igualmente, si no tienes derecho a pensión puedes
            tramitar una “negativa de pensión” y recuperar tus fondos de la Afore.
          </Text>
          <Title level={5}>Tienes semanas faltantes de cotización</Title>
          <Text type="pilarPage">
            En caso de no estar de acuerdo con el número de semanas cotizadas registradas en el
            IMSS, puedes solicitar una aclaración en línea o de manera presencial en la
            Subdelegación que te corresponda.
          </Text>
          <List
            textType="pilarPage"
            list={[
              '**En línea:** Realiza tu trámite en línea aquí. Primero solicita tu reporte de semanas cotizadas y posteriormente solicita una aclaración',
              '**Presencial:** En la Subdelegación que te corresponda, de lunes a viernes en días hábiles del IMSS de 8:00 a 15:30 horas.',
            ]}
            textStyles={css`
              margin-bottom: 10px;
            `}
          />
        </SubSection>
        <Title level={3} align="left" style={{ alignSelf: 'flex-start' }} medium>
          Recuperación de fondos de Afore
        </Title>
        <SubSection>
          <VideoPlayerContainer>
            <VideoPlayer url="https://www.youtube.com/watch?v=Xc0vgryw1WQ&list=PLR3Gc0jorI0KPhEWdwGAWyTI2ewhzUkEK&index=27&t=0s" />
          </VideoPlayerContainer>
          <Text type="pilarPage">
            Una vez tramitada tu pensión, tu Afore te devolverá tus ahorros contenidos en las
            siguientes subcuentas:
          </Text>
          <List
            textType="pilarPage"
            list={['SAR 92-97', 'Retiro 97', 'INFONAVIT 1997 y 1992', 'Aportaciones voluntarias']}
            textStyles={css`
              margin-bottom: 10px;
            `}
          />
          <Text type="pilarPage">
            La Afore en muchos casos funciona también como guía para tu proceso de pensión, por lo
            que te recomendamos acudir a ella antes de iniciar tu proceso de pensión.
          </Text>
        </SubSection>
      </Section>
    </FinautaPageBase>
  );
}
